import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { planQueueState, updatePlanQueueState } from './queue-state';
import { offerPublicationErrors } from './offer-validation';
import { PlanOfferEditor } from './PlanOfferEditor';
import { PlanManager } from './PlanManager';
import { PlanSetup } from './PlanSetup';
import { CreateStudentPlan } from './CreateStudentPlan';
import { RecordPlanPayment } from './RecordPlanPayment';
import { useUser } from '../../api/admin';
import { newOffer } from './offer-setup';
import { Page, PageHeader } from '../../components/Layout';
import { Modal } from '../../components/Modal';
import { Badge } from '../../components/Badge';
import { RequestsTable } from './RequestsTable';
import { OffersTable } from './OffersTable';
import { PlanAuditTable } from './PlanAuditTable';
import {
  useLearningPlans,
  useLearningPlanMutation,
  useSavePlanOffer,
  useTeachingResponses,
  useReplacementCandidates,
  usePlanSubjects,
} from '../../api/learning-plans';
import { learningPlanError, planLabel } from './labels';
import type {
  LearningPlan,
  PlanChange,
  PlanLesson,
  PlanOfferInput,
  PlanOffer,
} from '../../types/learning-plans';

const money = (n: number, currency: string) =>
  /^[A-Z]{3}$/.test(currency)
    ? new Intl.NumberFormat('ar-SA-u-nu-latn', { style: 'currency', currency }).format(n / 100)
    : `${(n / 100).toFixed(2)} ${currency}`;
const tabs = ['queue', 'offers', 'changes', 'closures', 'audit'] as const;
type Tab = (typeof tabs)[number];
const time = (s: string) => new Date(s).toLocaleString('ar-SA-u-ca-gregory-nu-latn', { timeZone: 'Asia/Riyadh' });

export function LearningPlansPage() {
  const mutation = useLearningPlanMutation();
  const saveOffer = useSavePlanOffer();
  // The tab and status filter live in the URL, so the home page's worklist
  // can link straight to them and a reload keeps what was open.
  const [params, setParams] = useSearchParams();
  const filters = planQueueState(params);
  const studentId = filters.studentId ?? '';
  const student = useUser(studentId);
  const tab: Tab = tabs.find((t) => t === params.get('tab')) ?? 'queue';
  const query = useLearningPlans(tab, filters);
  const requestStatus = filters.status ?? '';
  const setParam = (key: 'tab' | 'status', value: string) => setParams((current) => {
    const next = new URLSearchParams(current);
    if (value) next.set(key, value);
    else next.delete(key);
    return next;
  }, { replace: true });
  const setTab = (value: Tab) => setParam('tab', value === 'queue' ? '' : value);
  const setQueueParam = (key: 'q' | 'status' | 'sort' | 'page' | 'limit', value: string) =>
    setParams((current) => updatePlanQueueState(current, key, value), { replace: key === 'q' });
  const setRequestStatus = (value: string) => setQueueParam('status', value);
  const [showDecided, setShowDecided] = useState(false);
  const [editing, setEditing] = useState<PlanOfferInput | null>(null);
  const [versioning, setVersioning] = useState(false);
  const [offerAction, setOfferAction] = useState<{ offer: PlanOffer; kind: 'publish' | 'withdraw' | 'delete' } | null>(null);
  const [notice, setNotice] = useState('');
  const [creating, setCreating] = useState(false);
  const [renewing, setRenewing] = useState<LearningPlan | null>(null);
  const [recordingPayment, setRecordingPayment] = useState<LearningPlan | null>(null);
  const subjects = usePlanSubjects(creating || Boolean(renewing));
  const [settingUp, setSettingUp] = useState<LearningPlan | null>(null);
  const [resolving, setResolving] = useState<{
    request: LearningPlan;
    lesson?: PlanLesson;
    change?: PlanChange;
  } | null>(null);
  const [managing, setManaging] = useState<{ request: LearningPlan; change?: PlanChange } | null>(null);
  const [cancelling, setCancelling] = useState<LearningPlan | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [closureDate, setClosureDate] = useState('');
  const [closureReason, setClosureReason] = useState('');
  const run = (path: string, body: unknown, onSuccess?: () => void) =>
    mutation.mutate({ path, body }, { onSuccess });
  // Decided requests are history; what is open is the work.
  const shownChanges = query.data?.changes.filter((c) => showDecided || c.status === 'pending') ?? [];
  const offers = query.data?.offers ?? [];
  return (
    <div lang="ar" dir="rtl" className="learning-plans-ar">
    <Page>
      {mutation.isError && !offerAction && (
        <Modal lang="ar" dir="rtl" className="learning-plans-ar" open title="تعذّر الحفظ" onClose={() => mutation.reset()}>
          <p role="alert">
            {learningPlanError(mutation.error)}
          </p>
          <button type="button" className="btn btn-secondary" onClick={() => mutation.reset()}>
            العودة إلى التعديلات
          </button>
        </Modal>
      )}
      <PageHeader
        title={student.data ? `خطط ${student.data.fullName}` : 'الخطط التعليمية — 4 أسابيع'}
        description="طلبات الأسر والخطط المعروضة وطلبات التعديل في مكان واحد. جميع المواعيد بتوقيت الرياض."
        actions={<button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>إنشاء خطة لطالب</button>}
      />
      <div className="mb-4 flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            className={tab === t ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setTab(t)}
          >
            {planLabel(t)}
          </button>
        ))}
      </div>
      {notice && <p role="status" className="mb-4 rounded border border-rule bg-surface-2 p-3 text-sm">{notice}</p>}
      {query.isLoading && <p>جارٍ تحميل الخطط التعليمية…</p>}
      {query.isError && (
        <p role="alert" className="text-danger">
          {learningPlanError(query.error, 'تعذّر تحميل الخطط التعليمية. حاول مرة أخرى.')}
          <button type="button" className="btn btn-secondary ms-2" onClick={() => { void query.refetch(); }}>إعادة المحاولة</button>
        </p>
      )}
      {query.data && (
        <>
          {tab === 'queue' && (
            <RequestsTable
              requests={query.data.requests}
              meta={query.data.queue?.meta}
              counts={query.data.queue?.counts}
              search={filters.q ?? ''}
              onSearch={(value) => setQueueParam('q', value)}
              sort={filters.sort}
              onSort={(value) => setQueueParam('sort', value)}
              onPage={(value) => setQueueParam('page', String(value))}
              onLimit={(value) => setQueueParam('limit', String(value))}
              pending={query.isPlaceholderData}
              view={requestStatus}
              onView={setRequestStatus}
              onSetup={setSettingUp}
              onManage={(request) => setManaging({ request })}
              onLesson={(request, lesson) => setResolving({ request, lesson })}
              onCancel={(request) => { setCancelling(request); setCancelReason(''); }}
              onPayment={setRecordingPayment}
              onRenew={setRenewing}
            />
          )}
          {tab === 'offers' && (
            <OffersTable
              offers={offers}
              requests={query.data.requests}
              grades={query.data.grades}
              subjects={query.data.subjects}
              busy={mutation.isPending}
              onCreate={() => { setVersioning(false); saveOffer.reset(); setEditing(newOffer()); }}
              onEdit={(offer) => { saveOffer.reset(); setVersioning(true); setEditing(stripOffer(offer)); }}
              onAction={(offer, kind) => { mutation.reset(); setOfferAction({ offer, kind }); }}
            />
          )}
          {tab === 'changes' && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={showDecided} onChange={(event) => setShowDecided(event.target.checked)} />
                إظهار الطلبات المحسومة أيضًا
              </label>
              {shownChanges.length === 0 ? (
                <p className="panel p-6 text-center text-ink-2">{showDecided ? 'لا توجد طلبات من الأسر.' : 'لا توجد طلبات بانتظار ردك.'}</p>
              ) : (
                <ul className="panel divide-y divide-rule">
                  {shownChanges.map((c) => {
                    const request = query.data?.requests.find((r) => r.id === c.requestId);
                    return (
                      <li key={c.id} data-change-id={c.id} className="flex flex-wrap items-start justify-between gap-3 p-3">
                        <div className="min-w-0 flex-1 space-y-1">
                          <p className="flex flex-wrap items-center gap-2 font-medium">
                            {planLabel(`change.${c.kind}`)}
                            <Badge tone={c.status === 'pending' ? 'accent' : 'neutral'}>{planLabel(c.status)}</Badge>
                          </p>
                          <p className="text-sm text-ink-2">
                            {request?.studentName} · {request?.offer.nameAr || request?.offer.nameEn}
                            {' · '}{request?.offer.tracks.find((track) => track.key === c.trackKey)?.name ?? c.trackKey}
                            {' · '}<span className="tabular-nums">{time(c.createdAt)}</span>
                          </p>
                          <p className="text-sm">{c.reason}</p>
                          {c.decisionReason && <p className="text-sm text-ink-2">القرار: {c.decisionReason}</p>}
                        </div>
                        {c.status === 'pending' && request && (
                          <div className="flex flex-wrap gap-2">
                            {c.kind !== 'reschedule' && (
                              <button type="button" className="btn btn-primary" onClick={() => setManaging({ request, change: c })}>معالجة الطلب</button>
                            )}
                            <button type="button" className={c.kind === 'reschedule' ? 'btn btn-primary' : 'btn btn-secondary'}
                              onClick={() => setResolving({ request, change: c })}>
                              {c.kind === 'reschedule' ? 'المراجعة واتخاذ القرار' : 'رفض الطلب'}
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
          {tab === 'audit' && (
            <PlanAuditTable audit={query.data.audit} requests={query.data.requests} changes={query.data.changes} offers={offers} />
          )}
          {tab === 'closures' && (
            <section className="panel space-y-3 p-4">
              <h2 className="font-semibold">أيام إغلاق المنصة</h2>
              <p className="text-sm">
                الإغلاق بعد تأكيد الحجز يحفظ حق الأسرة في حصص بديلة مدفوعة. يجب إعادة ترتيب المقترحات غير المدفوعة المتأثرة. العطلات الرسمية لا تُعد أيام إغلاق تلقائيًا.
              </p>
              <label className="block">
                التاريخ
                <input
                  className="input"
                  type="date"
                  value={closureDate}
                  onChange={(e) => setClosureDate(e.target.value)}
                />
              </label>
              <label className="block">
                السبب
                <input
                  className="input"
                  value={closureReason}
                  onChange={(e) => setClosureReason(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="btn btn-primary"
                disabled={mutation.isPending || !closureDate || !closureReason.trim()}
                onClick={() =>
                  run(
                    '/admin/learning-plan-closures',
                    { date: closureDate, reason: closureReason },
                    () => {
                      setClosureDate('');
                      setClosureReason('');
                    },
                  )
                }
              >
                إضافة يوم إغلاق وحفظ الحصص البديلة
              </button>
              {query.data.closures.map((c) => (
                <p key={c.id}>
                  {c.closureDate} · {c.reason}
                </p>
              ))}
            </section>
          )}
          {cancelling && (
            <Modal
      lang="ar"
      dir="rtl"
      className="learning-plans-ar"
              open
              title="إغلاق الطلب غير المدفوع"
              onClose={() => setCancelling(null)}
              footer={
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={mutation.isPending || !cancelReason.trim()}
                  onClick={() =>
                    run(
                      `/admin/learning-plans/${cancelling.id}/cancel`,
                      { reason: cancelReason },
                      () => setCancelling(null),
                    )
                  }
                >
                  تحرير الحجوزات المؤقتة وإغلاق الطلب
                </button>
              }
            >
              <label className="block">
                السبب
                <textarea
                  className="input"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </label>
            </Modal>
          )}
          {offerAction && (
            <Modal lang="ar" dir="rtl" className="learning-plans-ar" open width="lg"
              title={offerAction.kind === 'publish' ? 'مراجعة الخطة ونشرها' : offerAction.kind === 'withdraw' ? 'سحب العرض من البيع' : 'حذف إصدار الخطة'}
              onClose={() => { if (!mutation.isPending) setOfferAction(null); }}
              footer={<>
                <button type="button" className="btn btn-secondary" disabled={mutation.isPending} onClick={() => setOfferAction(null)}>إلغاء</button>
                <button type="button" className="btn btn-primary" disabled={mutation.isPending || (offerAction.kind === 'publish' && offerPublicationErrors(offerAction.offer).length > 0)} onClick={() => {
                  const { offer, kind } = offerAction;
                  mutation.mutate({ path: `/admin/learning-plans/offers/${offer.id}${kind === 'delete' ? '' : '/publish'}`,
                    method: kind === 'delete' ? 'DELETE' : 'POST', body: kind === 'delete' ? undefined : { published: kind === 'publish' } },
                    { onSuccess: () => { setOfferAction(null); setNotice(kind === 'publish' ? 'تم نشر الخطة للأسر المؤهلة.' : kind === 'withdraw' ? 'تم سحب العرض وإيقاف الطلبات الجديدة.' : 'تم حذف إصدار الخطة.'); } });
                }}>{mutation.isPending ? 'جارٍ الحفظ…' : offerAction.kind === 'publish' ? 'تأكيد النشر' : offerAction.kind === 'withdraw' ? 'تأكيد السحب' : 'حذف الإصدار نهائيًا'}</button>
              </>}>
              <div className="space-y-3">
                {mutation.isError && <p role="alert" className="rounded border border-danger p-3 text-sm text-danger">{learningPlanError(mutation.error)}</p>}
                <h3 className="font-semibold">{offerAction.offer.nameAr} · الإصدار {offerAction.offer.version}</h3>
                {offerAction.kind === 'publish' && offerPublicationErrors(offerAction.offer).length > 0 && <div role="alert" className="rounded border border-danger p-3 text-sm">
                  <p className="font-semibold">هذه النسخة غير جاهزة للنشر</p>
                  <ul className="list-disc ps-5">{offerPublicationErrors(offerAction.offer).map((error) => <li key={error}>{error}</li>)}</ul>
                  <p className="mt-2">لتصحيح الشروط، أنشئ إصدارًا جديدًا بالقيم المطلوبة ثم احفظه وانشره.</p>
                  <button type="button" className="btn btn-secondary mt-2" disabled={mutation.isPending} onClick={() => {
                    saveOffer.reset(); setVersioning(true); setEditing(stripOffer(offerAction.offer)); setOfferAction(null);
                  }}>إنشاء إصدار مصحّح</button>
                </div>}
                <p>{money(offerAction.offer.priceCents, offerAction.offer.currency)} / 4 أسابيع (28 يومًا) · التجربة {money(offerAction.offer.trialPriceCents, offerAction.offer.currency)}</p>
                <p>الصفوف: {offerAction.offer.gradeIds.map((id) => query.data.grades.find((grade) => grade.id === id)?.nameAr ?? id).join('، ')}</p>
                {offerAction.offer.tracks.map((track) => <p key={track.key}>{track.name}: {track.weeklyMeetings} حصص أسبوعيًا · {track.meetings} حصص للدورة · {track.durationMinutes}–{track.maxDurationMinutes} دقيقة</p>)}
                <p className="rounded border border-rule bg-surface-2 p-3 text-sm">{offerAction.kind === 'publish'
                  ? 'ستظهر هذه الشروط للأسر المؤهلة، ويتوقف أي إصدار منشور آخر من الخطة نفسها عن استقبال طلبات جديدة. تبقى الطلبات الحالية بشروطها الأصلية.'
                  : offerAction.kind === 'withdraw' ? 'سيتوقف العرض عن استقبال طلبات جديدة. تبقى الطلبات والحصص الحالية محفوظة. يمكنك نشر العرض مجددًا لاحقًا.'
                  : 'سيُحذف هذا الإصدار نهائيًا. لا يمكن حذف إصدار منشور أو مرتبط بأي طلب طالب، حتى لو كان الطلب ملغيًا. يبقى سجل الإجراء محفوظًا.'}</p>
              </div>
            </Modal>
          )}
          {editing && (
            <PlanOfferEditor
              initial={editing}
              versioning={versioning}
              existingSlugs={query.data.offers.map((offer) => offer.slug)}
              subjects={query.data.subjects}
              busy={saveOffer.isPending}
              saveError={saveOffer.error}
              onClose={() => { if (!saveOffer.isPending) setEditing(null); }}
              onSave={(offer, publish) => saveOffer.mutate({ offer, publish }, { onSuccess: (result) => {
                setEditing(null);
                if (result.publicationError) {
                  setNotice(`تم حفظ المسودة، لكن تعذّر النشر: ${learningPlanError(result.publicationError)} يمكنك إعادة نشر النسخة المحفوظة دون إنشاء نسخة أخرى.`);
                  setOfferAction({ offer: result.offer, kind: 'publish' });
                } else setNotice(result.offer.published ? 'تم حفظ الخطة ونشرها للأسر المؤهلة.' : 'تم حفظ مسودة الخطة. اختر مراجعة ونشر لإتاحتها للأسر.');
              } })}
            />
          )}
          {(creating || renewing) && subjects.isLoading && <p role="status">جارٍ تحميل مواد الخطة…</p>}
          {(creating || renewing) && subjects.isError && <p role="alert">{learningPlanError(subjects.error)} <button type="button" className="btn btn-secondary" onClick={() => { void subjects.refetch(); }}>إعادة المحاولة</button></p>}
          {(creating || renewing) && subjects.data && <CreateStudentPlan studentId={studentId} renewal={renewing ?? undefined} subjects={subjects.data}
            onClose={() => { setCreating(false); setRenewing(null); }}
            onDone={(request) => { setCreating(false); setRenewing(null); setSettingUp(request); setNotice('تم إنشاء الطلب. عيّن المعلّم والجدول ثم سجّل الدفع.'); }} />}
          {recordingPayment && <RecordPlanPayment request={recordingPayment}
            onClose={() => setRecordingPayment(null)} onDone={() => { setRecordingPayment(null); setNotice('تم تسجيل الدفع وتأكيد الحصص.'); }} />}
          {settingUp && (
            <PlanSetup
              key={settingUp.id}
              request={settingUp}
              onClose={() => setSettingUp(null)}
              onDone={(message) => { setSettingUp(null); setNotice(message); }}
            />
          )}
          {managing && (
            <PlanManager
              key={`${managing.request.id}:${managing.change?.id ?? ''}`}
              {...managing}
              onClose={() => setManaging(null)}
              onDone={(message) => { setManaging(null); setNotice(message); }}
            />
          )}
          {resolving && (
            <SupportEditor
              key={resolving.lesson?.id ?? resolving.change?.id}
              {...resolving}
              busy={mutation.isPending}
              onClose={() => setResolving(null)}
              onSave={(path, body) => run(path, body, () => setResolving(null))}
            />
          )}
        </>
      )}
    </Page>
    </div>
  );
}

function stripOffer(o: PlanOffer): PlanOfferInput {
  const {
    id: _id,
    offerId: _offerId,
    version: _version,
    published: _published,
    trialAvailable: _trialAvailable,
    trialLimit: _trialLimit,
    trialsUsed: _trialsUsed,
    ...input
  } = o;
  return input;
}

function SupportEditor({
  request: r,
  lesson,
  change,
  busy,
  onClose,
  onSave,
}: {
  request: LearningPlan;
  lesson?: PlanLesson;
  change?: PlanChange;
  busy: boolean;
  onClose: () => void;
  onSave: (path: string, body: unknown) => void;
}) {
  const teachers = useTeachingResponses(r.id);
  // Weekly-time and teacher requests are approved by applying the change in
  // the plan manager; here they can only be declined.
  const declineOnly = Boolean(change && change.kind !== 'reschedule');
  const [reason, setReason] = useState('');
  const [start, setStart] = useState('');
  const [tutorId, setTutorId] = useState('');
  const [approved, setApproved] = useState(!declineOnly);
  const [agreed, setAgreed] = useState(false);
  const [outcome, setOutcome] = useState('');
  const needsTime = lesson?.status === 'owed' || change?.kind === 'reschedule';
  const trackKey = lesson?.trackKey ?? change?.trackKey;
  const target = { targetKind: change ? 'change' as const : 'lesson' as const,
    targetId: change?.id ?? lesson?.id ?? '', startTime: needsTime && start ? `${start}:00+03:00` : undefined };
  const canCoordinate = approved && Boolean(change || lesson?.status === 'owed') && (!needsTime || Boolean(start));
  const candidates = useReplacementCandidates(r.id, target, canCoordinate);
  const matchingResponses = teachers.data?.filter((t) => t.trackKey === trackKey &&
    (!t.replacement || (t.replacement.targetKind === target.targetKind && t.replacement.targetId === target.targetId &&
      (!needsTime || (target.startTime && t.replacement.appointments[0]?.startTime === new Date(target.startTime).toISOString())))));
  // Administration assigns directly, so every available teacher is selectable;
  // those who already accepted are listed first.
  const selectableTeachers = [
    ...(matchingResponses?.filter((t) => t.response === 'accepted') ?? [])
      .map((t) => ({ tutorId: t.tutorId, tutorName: t.tutorName ?? t.tutorId, accepted: true })),
    ...(canCoordinate ? candidates.data ?? [] : []).map((t) => ({ ...t, accepted: false })),
  ].filter((t, i, all) => all.findIndex((x) => x.tutorId === t.tutorId) === i);

  const save = () => {
    if (change)
      onSave(`/admin/learning-plan-changes/${change.id}/resolve`, {
        approved,
        familyAgreed: agreed,
        reason,
        startTime: start ? `${start}:00+03:00` : undefined,
        tutorId: tutorId || undefined,
      });
    else if (lesson?.status === 'owed')
      onSave(`/admin/learning-plan-lessons/${lesson.id}/replace`, {
        approved: true,
        familyAgreed: agreed,
        reason,
        startTime: `${start}:00+03:00`,
        tutorId: tutorId || undefined,
      });
    else if (lesson)
      onSave(`/admin/learning-plan-lessons/${lesson.id}/outcome`, {
        outcome,
        reason,
      });
  };
  const title = declineOnly ? 'رفض طلب الأسرة' : change ? 'نقل حصة بطلب الأسرة' : lesson?.status === 'owed' ? 'ترتيب حصة بديلة' : 'تسجيل غياب أو إيقاف إداري';
  return (
    <Modal
      lang="ar"
      dir="rtl"
      className="learning-plans-ar"
      open
      title={title}
      description={declineOnly
        ? 'للموافقة، طبّق التغيير من «إدارة الخطة». هنا يُرفض الطلب ويصل السبب للأسرة.'
        : 'تُحتسب تعديلات الطالب عند تأكيدها فقط. الحصص البديلة بسبب المعلّم أو المنصة لا تُحسب على الطالب.'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            إغلاق
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              busy ||
              !reason.trim() ||
              (lesson && lesson.status !== 'owed' && !outcome) ||
              (approved && (change || lesson?.status === 'owed') && !agreed) ||
              (approved && needsTime && !start)
            }
            onClick={save}
          >
            {declineOnly ? 'رفض الطلب' : 'حفظ القرار'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {change && <p className="rounded-lg border border-rule bg-surface-2 p-3 text-sm">{change.reason}</p>}
        {change && !declineOnly && (
          <fieldset className="flex gap-4 text-sm">
            <legend className="sr-only">القرار</legend>
            <label className="flex gap-2"><input type="radio" name="decision" checked={approved} onChange={() => setApproved(true)} />الموافقة</label>
            <label className="flex gap-2"><input type="radio" name="decision" checked={!approved} onChange={() => setApproved(false)} />الرفض</label>
          </fieldset>
        )}
        {lesson && lesson.status !== 'owed' && (
          <label className="block text-sm">
            سبب الغياب أو التدخل
            <select className="input" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
              <option value="" disabled>
                اختر السبب المتحقق منه للغياب أو التدخل
              </option>
              <option value="teacher_absent">غياب المعلّم أو إلغاؤه للحصة</option>
              <option value="platform_failure">عطل بالمنصة / إيقاف إداري</option>
              <option value="student_absent">غياب الطالب — تُستهلك الحصة المستحقة</option>
            </select>
          </label>
        )}
        {approved && (change || lesson?.status === 'owed') && (
          <>
            {needsTime && (
              <label className="block text-sm">
                الموعد الجديد بتوقيت الرياض
                <input
                  className="input"
                  type="datetime-local"
                  value={start}
                  onChange={(e) => { setStart(e.target.value); setTutorId(''); setAgreed(false); }}
                />
              </label>
            )}
            <label className="block text-sm">
              المعلّم
              <select
                className="input"
                value={tutorId}
                onChange={(e) => { setTutorId(e.target.value); setAgreed(false); }}
              >
                <option value="">الإبقاء على المعلّم الحالي</option>
                {selectableTeachers.map((t) => (
                  <option key={t.tutorId} value={t.tutorId}>
                    {t.tutorName}{t.accepted ? ' — وافق على الطلب' : ''}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-ink-3">
                {!canCoordinate ? 'أدخل الموعد أولًا لعرض المعلّمين المتاحين فيه.'
                  : candidates.isLoading ? 'جارٍ البحث عن المعلّمين المتاحين…'
                  : candidates.isError ? learningPlanError(candidates.error, 'تعذّر تحميل المعلّمين. أعد المحاولة.')
                  : 'يُعيَّن المعلّم مباشرة ويصله إشعار بالحصة.'}
              </span>
            </label>
            <label className="flex gap-2 text-sm">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              وافقت الأسرة على هذا الموعد والمعلّم
            </label>
          </>
        )}
        <label className="block text-sm">
          {declineOnly || !approved ? 'سبب الرفض (يصل للأسرة)' : 'السبب'}
          <textarea
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={1000}
          />
        </label>
      </div>
    </Modal>
  );
}
